import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { Users, ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';

export default function AppSubscribers() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeCompanyId } = useCompany();

  const [app, setApp] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;
  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    fetchAppDetails();
    fetchSubscribers();
  }, [appId, page, activeCompanyId]);

  const fetchAppDetails = async () => {
    try {
      const res = await api.get('/api/apps');
      const foundApp = res.data.find(a => a.id === parseInt(appId));
      setApp(foundApp);
    } catch (err) {
      console.error('Failed to fetch app details:', err);
    }
  };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/apps/${appId}/subscribers`, {
        params: { page, limit }
      });
      setSubscribers(res.data.subscribers || res.data || []);
      setTotalCount(res.data.total || res.data.length || 0);
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setSearchParams({ page: newPage.toString() });
    }
  };

  const getSubscriberTypeBadge = (type) => {
    const styles = {
      provider: 'bg-blue-100 text-blue-700 border-blue-200',
      hygienist: 'bg-green-100 text-green-700 border-green-200',
      dentaloffice: 'bg-purple-100 text-purple-700 border-purple-200',
    };
    return styles[type] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/apps')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to My Apps
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              App Subscribers
            </h1>
            {app && (
              <p className="text-gray-600 mt-1">
                Subscribers for <span className="font-semibold text-blue-600">{app.name}</span>
              </p>
            )}
          </div>
          
          <button
            onClick={fetchSubscribers}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 mb-6 text-white">
        <div className="flex items-center gap-3">
          <Users className="w-10 h-10 opacity-80" />
          <div>
            <p className="text-sm opacity-80">Total Active Subscribers</p>
            <p className="text-3xl font-bold">{totalCount}</p>
          </div>
        </div>
      </div>

      {/* Subscribers List */}
      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : subscribers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No active subscribers</p>
            <p className="text-sm">This app doesn't have any subscribers yet.</p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden sm:grid sm:grid-cols-8 gap-4 p-4 bg-gray-50 border-b font-semibold text-gray-700 text-sm" style={{gridTemplateColumns: '0.7fr 1fr 1fr 0.8fr 1.5fr 1fr 0.8fr 0.8fr'}}>
              <div>Subscriber ID</div>
              <div>Owner Name</div>
              <div>Subscriber Type</div>
              <div>App Type</div>
              <div>Email</div>
              <div>Contact Number</div>
              <div>Activated At</div>
              <div>Expire Date</div>
            </div>

            {/* Table Body */}
            <div className="divide-y">
              {subscribers.map((sub) => (
                <div
                  key={sub.id}
                  className="p-4 hover:bg-gray-50 transition"
                >
                  {/* Mobile View */}
                  <div className="sm:hidden space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-800">
                        {sub.owner_name ? sub.owner_name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : `Subscriber #${sub.legacy_subscriber_id}`}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getSubscriberTypeBadge(sub.legacy_subscriber_type)}`}>
                        {sub.legacy_subscriber_type ? sub.legacy_subscriber_type.charAt(0).toUpperCase() + sub.legacy_subscriber_type.slice(1).toLowerCase() : 'Unknown'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                      <p className="font-medium text-gray-700">ID: {sub.legacy_subscriber_id || '-'}</p>
                      {sub.app_type && <p>App Type: {sub.app_type.charAt(0).toUpperCase() + sub.app_type.slice(1).toLowerCase()}</p>}
                      {sub.email && <p>Email: {sub.email}</p>}
                      {sub.mobile_number && <p>Contact: {sub.mobile_number}</p>}
                      {sub.activated_at && (
                        <p>Activated: {new Date(sub.activated_at).toLocaleDateString()}</p>
                      )}
                      {sub.expire_date && (
                        <p>Expires: {new Date(sub.expire_date).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>

                  {/* Desktop View */}
                  <div className="hidden sm:grid sm:grid-cols-8 gap-4 items-center" style={{gridTemplateColumns: '0.7fr 1fr 1fr 0.8fr 1.5fr 1fr 0.8fr 0.8fr'}}>
                    <div className="font-mono text-sm text-gray-800">
                      {sub.legacy_subscriber_id || '-'}
                    </div>
                    <div className="font-medium text-gray-800">
                      {sub.owner_name ? sub.owner_name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : '-'}
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getSubscriberTypeBadge(sub.legacy_subscriber_type)}`}>
                        {sub.legacy_subscriber_type ? sub.legacy_subscriber_type.charAt(0).toUpperCase() + sub.legacy_subscriber_type.slice(1).toLowerCase() : 'Unknown'}
                      </span>
                    </div>
                    <div className="text-gray-600">
                      {sub.app_type ? sub.app_type.charAt(0).toUpperCase() + sub.app_type.slice(1).toLowerCase() : '-'}
                    </div>
                    <div className="text-gray-600 break-all text-sm">
                      {sub.email || '-'}
                    </div>
                    <div className="text-gray-600">
                      {sub.mobile_number || '-'}
                    </div>
                    <div className="text-gray-600">
                      {sub.activated_at ? new Date(sub.activated_at).toLocaleDateString() : '-'}
                    </div>
                    <div className="text-gray-600">
                      {sub.expire_date ? new Date(sub.expire_date).toLocaleDateString() : '-'}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                  Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalCount)} of {totalCount} subscribers
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg font-medium transition ${
                            page === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
